import React, { useRef, useEffect } from 'react';

// Remove conflicting global declaration

const P5Sketch: React.FC = () => {
  const sketchRef = useRef<HTMLDivElement>(null);
  const p5InstanceRef = useRef<any>(null);
  const canvasClickHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Cleanup any existing instance first
    if (p5InstanceRef.current) {
      p5InstanceRef.current.remove();
      p5InstanceRef.current = null;
    }

    const loadP5 = async () => {
      if (typeof window !== 'undefined') {
        const p5Module = await import('p5');
        const P5 = p5Module.default;

        const sketch = (p: any) => {
          // Font loading flag
          let __fontReady = false;
          
          // 1. 画布尺寸参数 - 响应式设计
          const getResponsiveSize = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            // 以容器实际宽度为准，避免画布比容器宽、右侧被 overflow-hidden 裁掉
            const containerW = sketchRef.current?.clientWidth || vw;
            
            // 移动端 (width < 768px)
            if (vw < 768) {
              return {
                width: Math.min(containerW, 600),
                height: Math.min(Math.max(vh - 160, 600), 680),
                scale: 0.7,  // 缩放因子
                singleHull: true // 窄屏不切成两栏，否则每行只能放两三个词
              };
            }
            // 平板端 (768px <= width < 1024px)
            else if (vw < 1024) {
              return {
                width: Math.min(containerW, 700),
                height: Math.min(vh - 150, 750),
                scale: 0.85,
                singleHull: false
              };
            }
            // 桌面端
            else {
              return {
                width: Math.min(containerW, 800),
                height: Math.min(vh - 100, 800),
                scale: 1.0,
                singleHull: false
              };
            }
          };
          // 触屏设备没有鼠标位置：触摸前把剑放在画布右下角，触摸后跟随触点
          const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
          let pointerSeen = hasFinePointer;
          
          let responsiveSize = getResponsiveSize();
          const canvasWidth  = responsiveSize.width;
          const canvasHeight = responsiveSize.height;
          const bgColor      = "#4C4137";  // 背景色 HEX

          // 2. 凸包形状参数
          const pointsCount       = 2000;  // 生成的点数
          const minPointsToSplit  = 6;     // 分裂凸包最少点数（hull 点数 >= 6 才允许分裂）

          // 3. 点集生成参数 - 响应式调整
          const clusterSize      = responsiveSize.scale < 1 ? 0.92 : 0.95;   // 移动端留出边距，避免文字贴边被裁

          // 4. 文字样式参数 - 响应式文字大小
          const getResponsiveTextParams = (scale: number) => {
            return {
              fontSize:      Math.floor(22 * scale),
              letterSpacing: 0.0,
              lineSpacing:   1.45,
              textColor:    "#151515",
              textMargin:    Math.floor(6 * scale),
              minFontSize:   Math.max(Math.floor(12 * scale), 11),
              maxFontSize:   Math.floor(24 * scale)
            };
          };
          
          const textParams = getResponsiveTextParams(responsiveSize.scale);
          


          // 6. 凸包颜色参数（主色 + 描边）
          const hullColor        = "#ECE1D6";  // 凸包主色（奶油米）
          const hullStroke       = "#C4A384";  // 描边颜色（杏金）
          const hullStrokeWeight = 1;          // 描边粗细（0 = 无描边）

          // 7. 文本内容参数 - 使用段落数组
          const paragraphs = [
            "Dear you,",
            "",
            "At night, when the window is quiet and the cursor blinks like a lighthouse, I hear a small wind over the keys and see a soft moon in the screen—tools that don't raise their voice yet find their own light on your face.",
            "",
            "If we meet inside the same page, let the interface be a window at dusk, air moving just enough to stir the thoughts you already have.",
            "A screen can keep a little moonlight, and code can leave room for weather.",
            "",
            "If you look up, I'll dim the lamp; if you lean in, I'll turn it brighter.",
            "",
            "Let's move at a human pace, lantern in hand, and let the road reveal itself."
          ];
          // --------------------------------------------------------------------
          // 内部映射（不改变上面的"参数表"格式；仅供代码使用）
          const CONFIG = {
            canvas: { fitToWindow: false, width: canvasWidth, height: canvasHeight, background: bgColor },
            hull:   { fill: hexToRgba(hullColor, 0.90), stroke: hullStroke, strokeWeight: hullStrokeWeight },
            text:   { color: textParams.textColor, size: textParams.fontSize, font: 'Homemade Apple',
                      lineHeight: textParams.lineSpacing, margin: textParams.textMargin,
                      repeat: false, justify: false },
            points: { count: pointsCount, spreadScale: clusterSize }
          };
          // --------------------------------------------------------------------

          let clusters: any[][] = [];
          let hulls: any[][] = [];
          // 实际使用的字号：从 textParams.fontSize 起，放不下就逐级缩小
          let fittedSize = textParams.fontSize;

          // Sword cursor variables
          let __swordAngle = -Math.PI/2;
          let __lastAngle  = -Math.PI/2;
          let __fxList: any[] = [];
          let __audioContext: AudioContext | null = null;
          let __audioBuffer: AudioBuffer | null = null;

          // Type definitions for better type safety
          interface Shard {
            p: any;
            v: any;
            r: number;
            a: number;
            s: number;
            life: number;
          }

          interface Crack {
            o: any;
            a: number;
            len: number;
            t: number;
            life: number;
            w: number;
          }

          interface GlassFx {
            t: number;
            life: number;
            p0: any;
            p1: any;
            angle: number;
            shards: Shard[];
            cracks: Crack[];
          }

          p.setup = async () => {
            // 更新响应式尺寸
            responsiveSize = getResponsiveSize();
            const w = responsiveSize.width;
            const h = responsiveSize.height;
            p.createCanvas(w, h);

            // 设置固定随机种子，让每次生成相同的形状
            p.randomSeed(12345);

            initPointsAndHulls();

            // Load audio for sword slash sound
            loadSlashSound();

            // Load Google Font - Homemade Apple
            if (!document.querySelector('link[href*="Homemade+Apple"]')) {
              const link = document.createElement('link');
              link.href = 'https://fonts.googleapis.com/css2?family=Homemade+Apple&display=swap';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }

            // Wait for font to be loaded
            try {
              await document.fonts.load(`${CONFIG.text.size}px "${CONFIG.text.font}"`);
              __fontReady = true;
            } catch (error) {
              console.warn('Font loading failed, using fallback');
              __fontReady = true; // Continue anyway
            }

            p.textFont(CONFIG.text.font);
            p.textAlign(p.LEFT, p.TOP);
            fitTextSize();
            
            // 添加canvas专用的点击事件监听器
            const canvas = p.canvas;
            if (canvas) {
              canvasClickHandlerRef.current = (e) => {
                e.stopPropagation(); // 阻止事件冒泡
                pointerSeen = true;
                const tip = swordTip();
                spawnGlassSlash(tip.x, tip.y, __swordAngle);
                playSlashSound();
              };
              canvas.addEventListener('mousedown', canvasClickHandlerRef.current);
            }
            
            if (hasFinePointer) p.noCursor(); // 用自绘的剑替代系统鼠标
          };

          p.draw = () => {
            // Don't render text until font is ready
            if (!__fontReady) return;
            
            p.background(CONFIG.canvas.background);

            // 画凸包形状
            if (CONFIG.hull.strokeWeight > 0) {
              p.stroke(CONFIG.hull.stroke);
              p.strokeWeight(CONFIG.hull.strokeWeight);
            } else p.noStroke();
            p.fill(CONFIG.hull.fill);
            for (let hull of hulls) {
              if (hull && hull.length > 3) {
                p.beginShape();
                for (let pt of hull) p.vertex(pt.x, pt.y);
                p.endShape(p.CLOSE);
              }
            }

            // 在所有凸包"并集区域"内排一次文字
            p.push();
            p.fill(CONFIG.text.color);
            applyTextSize(fittedSize);
            drawTextInPolygonsUnionByParagraphs(hulls, paragraphs, {
              size: fittedSize,
              lineHeight: CONFIG.text.lineHeight,
              padding: CONFIG.text.margin,
              repeat: CONFIG.text.repeat,
              justify: CONFIG.text.justify
            });
            p.pop();

            // 玻璃划痕特效 + 剑光标
            updateGlassFx();
            renderSwordCursor();
          };

          p.mouseReleased = () => {
            let pt = p.createVector(p.mouseX, p.mouseY);
            let argmin = -1;
            let minDist = p.width * p.height;

            for (let i = 0; i < clusters.length; i++) {
              for (let q of clusters[i]) {
                let d = distSquared(pt, q);
                if (d < minDist) { argmin = i; minDist = d; }
              }
            }

            if (argmin >= 0 && hulls[argmin] && hulls[argmin].length >= minPointsToSplit) {
              let clu = clusters.splice(argmin, 1)[0];
              let newClusters = divide(clu);
              clusters = [...clusters, ...newClusters];
              hulls.splice(argmin, 1);
              hulls = [...hulls, convexHull(newClusters[0]), convexHull(newClusters[1])];
              fitTextSize(); // 形状变了，重新确认文字放得下
            }
            return false;
          };

          p.touchStarted = () => { pointerSeen = true; };

          p.windowResized = () => {
            // 更新响应式参数
            responsiveSize = getResponsiveSize();
            const newWidth = responsiveSize.width;
            const newHeight = responsiveSize.height;
            
            // 更新文字参数
            const newTextParams = getResponsiveTextParams(responsiveSize.scale);
            Object.assign(textParams, newTextParams);
            
            // 调整画布大小
            p.resizeCanvas(newWidth, newHeight);
            
            // 重新初始化点和凸包
            initPointsAndHulls();
            if (__fontReady) fitTextSize();
          };

          // mousePressed事件现在由canvas的直接事件监听器处理

          // ---------- 初始化点云/分簇/凸包 ----------
          function initPointsAndHulls() {
            const syzeX = p.width * CONFIG.points.spreadScale;
            const syzeY = p.height * CONFIG.points.spreadScale;
            let points = [];
            for (let i = 0; i < CONFIG.points.count; i++) {
              points.push(p.createVector(
                p.width / 2 + p.random(-syzeX / 2, syzeX / 2),
                p.height / 2 + p.random(-syzeY / 2, syzeY / 2)
              ));
            }
            if (responsiveSize.singleHull) {
              clusters = [points];
              hulls = [convexHull(points)];
            } else {
              clusters = divide(points);
              hulls = [convexHull(clusters[0]), convexHull(clusters[1])];
            }
          }

          function applyTextSize(size: number) {
            p.textFont(CONFIG.text.font);
            p.textSize(size);
            p.textAlign(p.LEFT, p.TOP);
            p.textLeading(size * CONFIG.text.lineHeight);
            const ctx = p.canvas?.getContext('2d');
            if (ctx) ctx.font = `${size}px "${CONFIG.text.font}"`;
          }

          // 从目标字号开始试排，直到整封信都放得进纸里（或到最小字号为止）
          function fitTextSize() {
            let size = textParams.fontSize;
            while (size > textParams.minFontSize) {
              applyTextSize(size);
              const unplaced = drawTextInPolygonsUnionByParagraphs(hulls, paragraphs, {
                size,
                lineHeight: CONFIG.text.lineHeight,
                padding: CONFIG.text.margin,
                dry: true
              });
              if (unplaced === 0) break;
              size -= 1;
            }
            fittedSize = size;
          }

          // --------- K-means(2)一次划分 ----------
          function divide(points: any[]) {
            let newClusters: any[][] = [[], []];
            let centroids: any[] = [];
            for (let i = 0; i < 2; i++) {
              let c;
              do { c = p.random(points); } while (centroids.indexOf(c) !== -1);
              centroids.push(c);
            }
            for (let pt of points) {
              let argmin = 0;
              let minDist = distSquared(pt, centroids[0]);
              let d1 = distSquared(pt, centroids[1]);
              if (d1 < minDist) argmin = 1;
              newClusters[argmin].push(pt);
            }
            return newClusters;
          }

          // --------- Convex hull (Gift wrapping) ----------
          function convexHull(points: any[]) {
            let arr = points.slice();
            arr.sort((pt: any, q: any) => pt.x - q.x || pt.y - q.y);
            if (arr.length < 3) return arr;

            let hull = [];
            let pointOnHull = arr[0];
            let endPoint;

            do {
              hull.push(pointOnHull);
              endPoint = arr[0];
              for (let j = 1; j < arr.length; j++) {
                let pt = p.createVector(endPoint.x - pointOnHull.x, endPoint.y - pointOnHull.y);
                let q = p.createVector(arr[j].x - pointOnHull.x, arr[j].y - pointOnHull.y);
                if (endPoint.equals(pointOnHull) || pt.x * q.y - pt.y * q.x < 0) {
                  endPoint = arr[j];
                }
              }
              pointOnHull = endPoint;
            } while (!endPoint.equals(hull[0]));

            return hull;
          }

          function distSquared(pt: any, q: any) {
            return p.sq(pt.x - q.x) + p.sq(pt.y - q.y);
          }
// —— 手写体可读性：统一用“逐字绘制 + 人工字距/词距” —— //
// 响应式字距调整
const LETTER_SP = responsiveSize.scale * 1.2; // 每个非空白字符之间额外加的字距(px)
const WORD_SP   = responsiveSize.scale * 6;   // 单词后的词距(px)，不依赖字体的空格宽
const MIN_SPACE = responsiveSize.scale * 6;   // 空格的最小视觉宽度兜底

function spaceW(p:any){
  // 一些手写体的 ' ' 可能测到 0，这里兜底
  return Math.max(p.textWidth(' '), MIN_SPACE);
}

// 只测量（不绘制）一个 token 的宽度；token 内可能含空格
function widthOfToken(p:any, token:string){
  let w = 0;
  for (let i=0;i<token.length;i++){
    const ch = token[i];
    if (ch === ' ') { w += spaceW(p) + WORD_SP; continue; }
    w += p.textWidth(ch);
    // 下一位不是空格才加字距
    if (i < token.length-1 && token[i+1] !== ' ') w += LETTER_SP;
  }
  return w;
}

// 按“逐字 + 字距/词距”绘制一个 token，返回新的 x
function drawToken(p:any, token:string, x:number, y:number){
  for (let i=0;i<token.length;i++){
    const ch = token[i];
    if (ch === ' ') { x += spaceW(p) + WORD_SP; continue; }
    p.text(ch, x, y);
    x += p.textWidth(ch);
    if (i < token.length-1 && token[i+1] !== ' ') x += LETTER_SP;
  }
  return x;
}

          // ===================== 扫描线并集排版 =====================
          function drawTextInPolygonsUnionByParagraphs(polys: any[], paragraphArray: string[], opts: any): number {
            if (!polys || polys.length === 0) return Infinity;

            const dry = !!opts.dry; // 只测量不绘制
            const draw = (token: string, x: number, y: number) =>
              dry ? x + widthOfToken(p, token) : drawToken(p, token, x, y);
            let unplaced = 0;

            const pad = opts.padding || 0;
            const lh  = (opts.size || 16) * (opts.lineHeight || 1.3);
            
            // 统一 y 范围
            let minY = Infinity, maxY = -Infinity;
            for (const poly of polys) {
              if (!poly || poly.length < 3) continue;
              for (const pt of poly) { if (pt.y < minY) minY = pt.y; if (pt.y > maxY) maxY = pt.y; }
            }
            let yTop = Math.ceil(minY + pad + 40 * responsiveSize.scale); // 响应式向下平移
            let yEnd = Math.floor(maxY + 20 * responsiveSize.scale); // 响应式底部扩展

            let currentY = yTop;
            
            for (let paragraphIdx = 0; paragraphIdx < paragraphArray.length; paragraphIdx++) {
              const paragraph = paragraphArray[paragraphIdx];
              
              // 如果是空字符串，跳过一行创建间隔
              if (paragraph === "") {
                currentY += lh;
                continue;
              }
              
              // 将段落分词
              const words = paragraph.split(/\s+/).filter(w => w.length > 0);
              let wordIdx = 0;

              if (currentY > yEnd) { unplaced += words.length; continue; }
              
              // 为这个段落分配多行
              while (wordIdx < words.length && currentY <= yEnd) {
                const yMid = currentY + lh / 2;
                
                // 收集所有区间并做并集
                let intervals = [];
                for (const poly of polys) {
                  if (!poly || poly.length < 3) continue;
                  const xs = horizontalIntersections(poly, yMid);
                  for (let i = 0; i + 1 < xs.length; i += 2) {
                    intervals.push([Math.min(xs[i], xs[i+1]), Math.max(xs[i], xs[i+1])]);
                  }
                }
                intervals = mergeIntervals(intervals);
                
                if (intervals.length > 0) {
                  // 在这一行渲染单词
                  for (let k = 0; k < intervals.length && wordIdx < words.length; k++) {
                    let xL = intervals[k][0] + pad;
                    let xR = intervals[k][1] - pad;
                    if (xR - xL <= 1) continue;

                    let x = xL;
                    while (x < xR - 0.5 && wordIdx < words.length) {
                      const word  = words[wordIdx];
                      const token = word + (wordIdx < words.length - 1 ? ' ' : ''); // 单词+空格

                      const tokenW = widthOfToken(p, token);

                      // 1) 完整 token 能放下：正常绘制（逐字 + 字距/词距）
                      if (x + tokenW <= xR) {
                        x = draw(token, x, currentY);
                        wordIdx++;
                        continue;
                      }

                      // 2) 放不下：若此行是新行开头(x==xL)，尝试加连字符分词；否则直接换行
                      if (x === xL) {
                        // 计算能放下的最大前缀，末尾加连字符 '-'
                        let fit = 0;
                        for (let k = 1; k <= word.length; k++) {
                          const w = widthOfToken(p, word.slice(0, k) + '-');
                          if (x + w <= xR) fit = k; else break;
                        }
                        if (fit > 0) {
                          x = draw(word.slice(0, fit) + '-', x, currentY);
                          // 剩余部分回写到当前单词（不带空格）
                          words[wordIdx] = word.slice(fit);
                        } else {
                          break; // 连一个字符都放不下，防止死循环
                        }
                      } else {
                        break; // 此行剩余空间不够，换到下一条 interval / 下一行
                      }
                    }
                  }
                }
                
                currentY += lh; // 移到下一行
              }
              unplaced += words.length - wordIdx;
            }
            return unplaced;
          }


          // 区间并集
          function mergeIntervals(list: any[]) {
            if (!list || list.length === 0) return [];
            list.sort((a: any, b: any) => a[0] - b[0]);
            const out = [list[0].slice()];
            for (let i = 1; i < list.length; i++) {
              const cur = list[i], last = out[out.length - 1];
              if (cur[0] <= last[1]) last[1] = Math.max(last[1], cur[1]);
              else out.push(cur.slice());
            }
            return out;
          }

          // y 水平线与多边形交点
          function horizontalIntersections(poly: any[], y: number) {
            const xs = [];
            for (let i = 0; i < poly.length; i++) {
              const a = poly[i], b = poly[(i + 1) % poly.length];
              const y1 = a.y, y2 = b.y;
              if ((y1 <= y && y < y2) || (y2 <= y && y < y1)) {
                const t = (y - y1) / (y2 - y1);
                xs.push(a.x + t * (b.x - a.x));
              }
            }
            xs.sort((m: number, n: number) => m - n);
            return xs;
          }



          // ----------------- 工具：HEX -> rgba() 字符串 -----------------
          function hexToRgba(hex: string, a = 1) {
            const h = hex.replace('#','');
            const bigint = parseInt(h, 16);
            const r = (h.length===3) ? ((bigint>>8) & 0xF)*17 : (bigint>>16)&255;
            const g = (h.length===3) ? ((bigint>>4) & 0xF)*17 : (bigint>>8)&255;
            const b = (h.length===3) ? (bigint    & 0xF)*17 : (bigint   )&255;
            return `rgba(${r},${g},${b},${a})`;
          }

          // ===================== 中古剑光标 + 玻璃划痕特效 =====================
          // 剑柄所在位置：有指针就跟随指针，否则停在画布右下角
          function swordAnchor() {
            if (pointerSeen) return { x: p.mouseX, y: p.mouseY };
            return { x: p.width * 0.8, y: p.height * 0.86 };
          }

          function renderSwordCursor() {
            const dx = p.mouseX - p.pmouseX, dy = p.mouseY - p.pmouseY;
            if (pointerSeen && Math.abs(dx) + Math.abs(dy) > 0.2) {
              __swordAngle = lerpAngle(__swordAngle, Math.atan2(dy, dx), 0.35);
              __lastAngle = __swordAngle;
            } else __swordAngle = __lastAngle;

            const anchor = swordAnchor();
            p.push();
            p.translate(anchor.x, anchor.y);
            p.rotate(__swordAngle);

            const steel  = p.color(236,225,214,240); // #ECE1D6
            const steel2 = p.color(255,255,255,140);
            const guardC = p.color('#C4A384');
            const gripC  = p.color(40,40,40,240);

            p.noStroke();
            p.fill(steel);  
            p.quad(0,-3, 100,-1, 100,1, 0,3); 
            p.triangle(100,-1, 116,0, 100,1);
            p.stroke(steel2); 
            p.strokeWeight(1.2); 
            p.line(8,-0.5,96,-0.5); 
            p.line(8,0.5,96,0.5);
            p.noStroke(); 
            p.fill(guardC); 
            p.rect(-10,-14,6,28,2); 
            p.rect(-16,-4,20,8,3);
            p.fill(gripC); 
            p.rect(-40,-4.5,22,9,3); 
            p.fill(guardC); 
            p.ellipse(-44,0,10,10);
            p.pop();
          }

          function swordTip() {
            const len = 110;
            const anchor = swordAnchor();
            return p.createVector(anchor.x + Math.cos(__swordAngle) * len, anchor.y + Math.sin(__swordAngle) * len);
          }

          function spawnGlassSlash(x: number, y: number, angle: number) {
            // Create direction vector using trigonometry
            const dir = p.createVector(Math.cos(angle), Math.sin(angle));
            const p0 = p.createVector(x, y).add(p.createVector(dir.x * -40, dir.y * -40));
            const p1 = p.createVector(x, y).add(p.createVector(dir.x * 170, dir.y * 170));
            const fx: GlassFx = { t:0, life:42, p0, p1, angle, shards:[], cracks:[] };

            const seeds = [0.15, 0.5, 0.85];
            for (const u of seeds) {
              const pos = p.createVector(
                p.lerp(p0.x, p1.x, u), 
                p.lerp(p0.y, p1.y, u)
              );
              const rayCount = 8;
              for (let i=0;i<rayCount;i++){
                fx.cracks.push({ 
                  o: pos.copy(), 
                  a: angle + (p.random(-0.9,0.9)+(i/rayCount-0.5)*0.6),
                  len: p.random(30,100), 
                  t:0, 
                  life: p.random(24,42), 
                  w: p.random(0.6,1.2) 
                });
              }
              const shardN = 10;
              for (let i=0;i<shardN;i++){
                const vAngle = angle + p.random(-0.6,0.6) + Math.sign(p.random()-0.5)*Math.PI/2;
                const vMag = p.random(1.5,4);
                const v = p.createVector(Math.cos(vAngle) * vMag, Math.sin(vAngle) * vMag);
                const randomOffset = p.createVector(p.random(-6, 6), p.random(-6, 6));
                fx.shards.push({ 
                  p: pos.copy().add(randomOffset), 
                  v, 
                  r: p.random(-0.2,0.2),
                  a: p.random(p.TWO_PI), 
                  s: p.random(4,10), 
                  life: p.random(28,46) 
                });
              }
            }
            __fxList.push(fx);
          }

          function updateGlassFx() {
            for (let i = __fxList.length-1; i>=0; i--) {
              const f = __fxList[i]; f.t++;

              p.push(); 
              p.blendMode(p.SCREEN);
              const prog = easeOutCubic(p.constrain(f.t/f.life,0,1));
              p.stroke(255,240,220, 180*(1-prog)); 
              p.strokeWeight(2.2*(1-prog)+0.8);
              p.line(f.p0.x, f.p0.y, f.p1.x, f.p1.y); 
              p.pop();

              for (const r of f.cracks) {
                r.t++; 
                const k = p.constrain(r.t/r.life,0,1); 
                const L = r.len*easeOutCubic(k);
                const x2 = r.o.x + Math.cos(r.a)*L, y2 = r.o.y + Math.sin(r.a)*L;
                p.stroke(200,200,200,180*(1-k)); 
                p.strokeWeight(r.w*(1-k)+0.4); 
                p.line(r.o.x,r.o.y,x2,y2);
                if (k>0.35){ 
                  const b=r.a+p.random(-0.5,0.5), l2=L*p.random(0.15,0.35);
                  p.line(r.o.x+Math.cos(r.a)*L*0.6, r.o.y+Math.sin(r.a)*L*0.6,
                       r.o.x+Math.cos(r.a)*L*0.6+Math.cos(b)*l2, r.o.y+Math.sin(r.a)*L*0.6+Math.sin(b)*l2); 
                }
              }

              for (const s of f.shards){
                s.life--; 
                s.v.mult(0.97); 
                s.v.y += 0.02; 
                s.p.add(s.v); 
                s.a += s.r;
                const alpha = p.map(s.life, 0, 46, 0, 220);
                p.push(); 
                p.translate(s.p.x,s.p.y); 
                p.rotate(s.a); 
                p.noStroke(); 
                p.fill(236,225,214,alpha);
                p.triangle(-s.s*0.6,-s.s*0.4, s.s*0.8,-s.s*0.2, -s.s*0.2, s.s*0.7); 
                p.pop();
              }

              f.shards = f.shards.filter((s: any)=>s.life>0);
              if (f.t>f.life && f.shards.length===0) __fxList.splice(i,1);
            }
          }

          function lerpAngle(a: number,b: number,t: number){ 
            const d=Math.atan2(Math.sin(b-a),Math.cos(b-a)); 
            return a + d*t; 
          }

          function easeOutCubic(x: number){ 
            return 1 - Math.pow(1 - x, 3); 
          }

          // ===================== 音频处理 =====================
          async function loadSlashSound() {
            try {
              if (!__audioContext) {
                __audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
              }
              
              const response = await fetch('/sounds/paper.mp3');
              const arrayBuffer = await response.arrayBuffer();
              __audioBuffer = await __audioContext.decodeAudioData(arrayBuffer);
            } catch (error) {
              console.warn('Failed to load slash sound:', error);
            }
          }

          function playSlashSound() {
            if (__audioContext && __audioBuffer) {
              try {
                // Resume context if needed (for browser autoplay policy)
                if (__audioContext.state === 'suspended') {
                  __audioContext.resume();
                }
                
                const source = __audioContext.createBufferSource();
                const gainNode = __audioContext.createGain();
                
                source.buffer = __audioBuffer;
                gainNode.gain.value = 0.3; // 调节音量
                
                source.connect(gainNode);
                gainNode.connect(__audioContext.destination);
                
                source.start(0);
              } catch (error) {
                console.warn('Failed to play slash sound:', error);
              }
            }
          }
        };

        if (sketchRef.current && !p5InstanceRef.current) {
          p5InstanceRef.current = new P5(sketch, sketchRef.current);
        }
      }
    };

    loadP5();

    return () => {
      if (p5InstanceRef.current) {
        // 清理事件监听器
        if (p5InstanceRef.current.canvas && canvasClickHandlerRef.current) {
          p5InstanceRef.current.canvas.removeEventListener('mousedown', canvasClickHandlerRef.current);
        }
        p5InstanceRef.current.remove();
        canvasClickHandlerRef.current = null;
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="w-full flex justify-center items-center px-0 sm:px-6 lg:px-8">
      <div ref={sketchRef} className="w-full max-w-4xl rounded-lg overflow-hidden"></div>
    </div>
  );
};

export default P5Sketch;